import { StoreAsset, storeAssets } from '~/constants/stores';
import {
  BusinessNeed,
  BusinessType,
  getOnboardingStructure,
  NavigationItem,
  OnboardingLocalStorageKeys,
  OrdersMechanism,
  ViewComponent,
  ViewKey,
} from '~/pages/Onboarding/onboarding.constants';
import {
  OnboardingActionKeys,
  OnboardingActionTypes,
} from '~/pages/Onboarding/store/onboarding.actions';
import Address, { Country } from '~/types/Address';
import Store, { StoreStatusName } from '~/types/Store';

export interface OnboardingViewBasics {
  viewComponents: ViewComponent[];
}

export interface OnboardingInfoState extends OnboardingViewBasics {
  error?: Error | string | null;
  loading: boolean;
  businessType: BusinessType;
  businessNeed: keyof typeof BusinessNeed | undefined;
  country: Maybe<Country>;
  phone: string | null;
  companyName: string | null;
  monthlyVolume: string | null;
  name?: string | null;
  info?: string | null;
  company?: string | null;
  email?: string | null;
}

export interface OnboardingPreferencesState extends OnboardingViewBasics {
  error?: Error | string | null;
  loading: boolean;
  primaryCarrier: string | number | undefined;
  labelSize: string | number | undefined;
  address: Address | null;
  addressObject: Address | null;
}

export interface OnboardingOrdersState extends OnboardingViewBasics {
  error?: Error | string | null;
  loading: boolean;
  orderMechanism: OrdersMechanism;
  storeSelection: StoreAsset | null;
  address: Maybe<Address>;
  addressObject: Address | null;
  storeConnectProps: string;
}

export interface OnboardingProPlanState extends OnboardingViewBasics {
  error?: Error | string | null;
  loading: boolean;
  plan: string | null;
}

export interface OnboardingApi extends OnboardingViewBasics {
  hasSubmittedHubspotForm: boolean;
}

export interface OnboardingState {
  active: boolean;
  ready: boolean;
  connectedStore: Store | null;
  error?: Error | string | null;
  navigation: {
    steps: NavigationItem[];
    currentStepIndex: number;
    lastCompletedStepIndex: number;
  };
  [ViewKey.info]: OnboardingInfoState;
  [ViewKey.preferences]: OnboardingPreferencesState;
  [ViewKey.orders]: OnboardingOrdersState;
  [ViewKey.api]: OnboardingApi;
  [ViewKey.getting_started]: OnboardingViewBasics;
  [ViewKey.shipping_details]: OnboardingViewBasics;
  [ViewKey.pro_plan]: OnboardingProPlanState;
}

const getPersistedOnboardingStatus = (): boolean => {
  const value = localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_ACTIVE);
  return !!(value && value === 'true');
};

const getPersistedBusinessNeed = (): keyof typeof BusinessNeed | undefined => {
  const value = localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_NEED);
  return value && value !== 'undefined' ? (value as keyof typeof BusinessNeed) : undefined;
};

const getPersistedBusinessType = (): BusinessType => {
  const value = localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_TYPE);
  return value ? (value as BusinessType) : BusinessType.BUSINESS;
};

const getPersistedCurrentStep = (): number => {
  const value = localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_CURRENT_STEP);
  return value ? parseInt(value, 10) : 0;
};

const getPersistedLastCompletedStep = (): number => {
  const value = localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_LAST_COMPLETED_STEP);
  return value ? parseInt(value, 10) : 0;
};

const getPersistedConnectedStore = (): string | undefined => {
  const value = localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_ACTIVE_STORE);
  return value ? value : undefined;
};

const getPersistedUserCountry = (): Maybe<Country> => {
  const value = localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_USER_COUNTRY);

  if (value) {
    const country: Maybe<Country> = JSON.parse(value);
    return country;
  } else {
    return null;
  }
};

const getPersistedPlan = (): string | null =>
  localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_PLAN);

const initialOnboardingStructure = getOnboardingStructure(
  getPersistedBusinessType(),
  getPersistedBusinessNeed(),
  getPersistedConnectedStore(),
);

/**
 * Checks if the last completed index from localstorage is out of bounds with current nav items
 * @param navitems
 */
const getProtectedLastCompleted = (navitems: NavigationItem[]) => {
  const localLastCompleted = getPersistedLastCompletedStep();
  const maxNavItemIndex = navitems.length - 1;
  if (localLastCompleted > maxNavItemIndex) {
    localStorage.setItem(
      OnboardingLocalStorageKeys.ONBOARDING_LAST_COMPLETED_STEP,
      maxNavItemIndex.toString(),
    );
    return maxNavItemIndex;
  } else {
    return localLastCompleted;
  }
};

export const initialState: OnboardingState = {
  active: getPersistedOnboardingStatus(),
  ready: false,
  connectedStore: null,
  error: null,
  navigation: {
    steps: initialOnboardingStructure.navigation,
    currentStepIndex: getPersistedCurrentStep(),
    lastCompletedStepIndex: getProtectedLastCompleted(initialOnboardingStructure.navigation),
  },
  [ViewKey.info]: {
    viewComponents: initialOnboardingStructure.viewComponents[ViewKey.info],
    businessType: getPersistedBusinessType(),
    businessNeed: getPersistedBusinessNeed(),
    country: getPersistedUserCountry(),
    phone: null,
    companyName: null,
    monthlyVolume: null,
    loading: false,
    error: null,
  },
  [ViewKey.preferences]: {
    viewComponents: initialOnboardingStructure.viewComponents[ViewKey.preferences],
    error: null,
    primaryCarrier: '',
    labelSize: '',
    loading: false,
    address: null,
    addressObject: null,
  },
  [ViewKey.orders]: {
    viewComponents: initialOnboardingStructure.viewComponents[ViewKey.orders],
    orderMechanism: initialOnboardingStructure.defaultOrdersMechanism,
    error: null,
    loading: false,
    storeSelection: null,
    address: null,
    addressObject: null,
    storeConnectProps: '',
  },
  [ViewKey.api]: {
    viewComponents: initialOnboardingStructure.viewComponents[ViewKey.api],
    hasSubmittedHubspotForm: false,
  },
  [ViewKey.getting_started]: {
    viewComponents: initialOnboardingStructure.viewComponents[ViewKey.getting_started],
  },
  [ViewKey.shipping_details]: {
    viewComponents: initialOnboardingStructure.viewComponents[ViewKey.shipping_details],
  },
  [ViewKey.pro_plan]: {
    viewComponents: initialOnboardingStructure.viewComponents[ViewKey.pro_plan],
    error: null,
    loading: false,
    plan: getPersistedPlan(),
  },
};

function reducer(
  state: OnboardingState = initialState,
  action: OnboardingActionTypes,
): OnboardingState {
  switch (action.type) {
    case OnboardingActionKeys.START_ONBOARDING:
      return {
        ...state,
        active: true,
      };
    case OnboardingActionKeys.PREPOPULATE_ONBOARDING:
      const connectedStores = action.payload.values.connectedStores.filter(
        (store) => store?.status?.name === StoreStatusName.Connected,
      );
      // Get store asset from connected store, otherwise null
      const getStoreSelection = () => {
        return connectedStores.length && connectedStores[0].storePlatform.name
          ? storeAssets[connectedStores[0].storePlatform.name]
          : null;
      };
      return {
        ...state,
        ready: true,
        connectedStore: connectedStores.length ? connectedStores[0] : null,
        [ViewKey.info]: {
          ...state[ViewKey.info],
          name: action.payload.values.name,
          email: action.payload.values.email,
          phone: action.payload.values.phone,
          company: action.payload.values.company,
        },
        [ViewKey.orders]: {
          ...state[ViewKey.orders],
          addressObject: action.payload.values.address,
          storeSelection: getStoreSelection(),
        },
        [ViewKey.preferences]: {
          ...state[ViewKey.preferences],
          addressObject: action.payload.values.address,
          labelSize: action.payload.values.defaultLabelFormat,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_STRUCTURE:
      const newOnboardingStructure = {
        ...getOnboardingStructure(
          action.payload.businessType,
          action.payload.businessNeed,
          action.payload.storeName,
        ),
      };
      return {
        ...state,
        navigation: {
          ...state.navigation,
          steps: newOnboardingStructure.navigation,
        },
        [ViewKey.info]: {
          ...state[ViewKey.info],
          viewComponents: newOnboardingStructure.viewComponents[ViewKey.info],
          businessType: action.payload.businessType,
          businessNeed: action.payload.businessNeed,
        },
        [ViewKey.preferences]: {
          ...state[ViewKey.preferences],
          viewComponents: newOnboardingStructure.viewComponents[ViewKey.preferences],
        },
        [ViewKey.orders]: {
          ...state[ViewKey.orders],
          viewComponents: newOnboardingStructure.viewComponents[ViewKey.orders],
          orderMechanism: newOnboardingStructure.defaultOrdersMechanism,
        },
        [ViewKey.api]: {
          ...state[ViewKey.api],
          viewComponents: newOnboardingStructure.viewComponents[ViewKey.api],
        },
        [ViewKey.getting_started]: {
          ...state[ViewKey.getting_started],
          viewComponents: newOnboardingStructure.viewComponents[ViewKey.getting_started],
        },
        [ViewKey.shipping_details]: {
          ...state[ViewKey.shipping_details],
          viewComponents: newOnboardingStructure.viewComponents[ViewKey.shipping_details],
        },
      };
    case OnboardingActionKeys.NAVIGATE:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          currentStepIndex: action.payload.index,
        },
      };
    case OnboardingActionKeys.COMPLETE_STEP:
      return {
        ...state,
        navigation: {
          ...state.navigation,
          lastCompletedStepIndex:
            action.payload.index <= state.navigation.steps.length - 1
              ? action.payload.index
              : state.navigation.steps.length - 1,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_INFO:
      return {
        ...state,
        [ViewKey.info]: {
          ...state[ViewKey.info],
          loading: true,
          error: null,
          ...action.payload.values,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_INFO_SUCCESS:
      return {
        ...state,
        [ViewKey.info]: {
          ...state[ViewKey.info],
          loading: false,
          error: null,
          ...action.payload.values,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_INFO_ERROR:
      return {
        ...state,
        [ViewKey.info]: {
          ...state[ViewKey.info],
          loading: false,
          error: action.payload.error,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES:
      return {
        ...state,
        [ViewKey.preferences]: {
          ...state[ViewKey.preferences],
          error: null,
          loading: true,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES_SUCCESS:
      return {
        ...state,
        [ViewKey.preferences]: {
          ...state[ViewKey.preferences],
          error: null,
          loading: false,
          primaryCarrier: action.payload.primaryCarrier,
          labelSize: action.payload.labelSize,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_PREFERENCES_ERROR:
      return {
        ...state,
        [ViewKey.preferences]: {
          ...state[ViewKey.preferences],
          error: action.payload.error,
          loading: false,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_PRO_PLAN:
      return {
        ...state,
        [ViewKey.pro_plan]: {
          ...state[ViewKey.pro_plan],
          loading: false,
          error: null,
          plan: action.payload.plan,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_ORDERS:
      return {
        ...state,
        [ViewKey.orders]: {
          ...state[ViewKey.orders],
          error: null,
          loading: true,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_ORDERS_SUCCESS:
      return {
        ...state,
        [ViewKey.orders]: {
          ...state[ViewKey.orders],
          error: null,
          loading: false,
          orderMechanism: action.payload.orderMechanism,
          storeSelection: action.payload.storeSelection,
          address: action.payload.address,
          storeConnectProps: action.payload.storeConnectProps,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_ORDERS_ERROR:
      return {
        ...state,
        [ViewKey.orders]: {
          ...state[ViewKey.orders],
          error: action.payload.error,
          loading: false,
        },
      };
    case OnboardingActionKeys.COMPLETE_ONBOARDING:
      return {
        ...state,
        active: false,
      };
    case OnboardingActionKeys.UPDATE_HAS_SUBMITTED_HUBSPOT_FORM:
      return {
        ...state,
        [ViewKey.api]: {
          ...state[ViewKey.api],
          hasSubmittedHubspotForm: true,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_ADDRESSES:
      return {
        ...state,
        [ViewKey.orders]: {
          ...state[ViewKey.orders],
          addressObject: action.payload.address,
        },
        [ViewKey.preferences]: {
          ...state[ViewKey.preferences],
          addressObject: action.payload.address,
        },
      };
    case OnboardingActionKeys.CLEAR_GLOBAL_ERRORS:
      return {
        ...state,
        error: null,
        [ViewKey.info]: {
          ...state[ViewKey.info],
          error: null,
        },
        [ViewKey.preferences]: {
          ...state[ViewKey.preferences],
          error: null,
        },
        [ViewKey.orders]: {
          ...state[ViewKey.orders],
          error: null,
        },
      };
    case OnboardingActionKeys.UPDATE_ONBOARDING_PRO_PLAN_ERROR:
      return {
        ...state,
        [ViewKey.pro_plan]: {
          ...state[ViewKey.pro_plan],
          error: action.payload.error,
          loading: false,
        },
      };
    default:
      return state;
  }
}

export default reducer;
